import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DOCUMENT_TYPES, type DocumentType } from "~/config/documents";
import { PUSH_NOTIFICATION_MESSAGES } from "~/constants/messages/pushNotificationMessages";
import { useWebPubSub } from "~/features/projectTop/webPubSubContext";
import { useOrganization } from "~/hooks/useOrganization";
import { setCurrentProjectOutputsDocumentType } from "~/slices/auth";
import type { RootState } from "~/store";
import { useNativePushNotification } from "~/utils/useNativePushNotification";
import { registerGroupMessageHandler } from "~/utils/webPubSubUtils";

export const useWebPubSubMessageHandler = (): void => {
  const dispatch = useDispatch();
  const { currentProject, currentUser } = useSelector((state: RootState) => state.auth);
  const { client, setClient } = useWebPubSub();
  const { sendNotificationWithNavigation } = useNativePushNotification();
  const { refresh: refreshOrganization } = useOrganization(currentUser?.organization_id);

  const successHandlerRef = useRef<
    (data: { type: string; id: string; project_id: string }) => void
  >(() => {});
  const failureHandlerRef = useRef<
    (data: { type: string; error: string; project_id: string; error_type?: string }) => void
  >(() => {});

  successHandlerRef.current = (data) => {
    console.log("[webpubsub] handleSuccessfulMessage called:", {
      source: "webpubsub",
      currentProjectId: currentProject?.id,
      incomingProjectId: data.project_id,
      documentType: data.type,
      documentId: data.id,
    });

    const documentType = data.type as DocumentType;
    const documentConfig = DOCUMENT_TYPES[documentType as keyof typeof DOCUMENT_TYPES];

    if (!documentConfig) {
      console.error(`Unknown document type: ${data.type}`);
      return;
    }

    const isMatchingProject = data.project_id === currentProject?.id;

    if (isMatchingProject) {
      console.log("[webpubsub] Processing message - dispatching to Redux");
      dispatch(
        setCurrentProjectOutputsDocumentType({
          documentType,
          documentId: data.id,
          isGenerating: false,
        }),
      );
      toast.success(`${documentConfig.name}の生成が完了しました`, {
        toastId: `generation-success-${data.type}-${data.project_id}`,
      });
      sendNotificationWithNavigation({
        title: PUSH_NOTIFICATION_MESSAGES.DOCUMENT_GENERATION.SUCCESS_TITLE,
        message: PUSH_NOTIFICATION_MESSAGES.DOCUMENT_GENERATION.SUCCESS_SAME_PROJECT(
          currentProject.name,
          documentConfig.name,
        ),
        organization_id: currentUser.organization_id,
        project_id: currentProject.id,
        document_type: data.type,
        document_id: data.id,
      });
    } else {
      console.log(
        `[webpubsub] Showing notification for different project: ${data.project_id} (current: ${currentProject?.id})`,
      );
      sendNotificationWithNavigation({
        title: PUSH_NOTIFICATION_MESSAGES.DOCUMENT_GENERATION.SUCCESS_TITLE,
        message: PUSH_NOTIFICATION_MESSAGES.DOCUMENT_GENERATION.SUCCESS_OTHER_PROJECT(
          documentConfig.name,
        ),
        organization_id: currentUser?.organization_id,
        project_id: data.project_id,
        document_type: data.type,
        document_id: data.id,
      });
    }
  };

  failureHandlerRef.current = (data) => {
    console.log("[webpubsub] handleFailureMessage called:", {
      source: "webpubsub",
      currentProjectId: currentProject?.id,
      incomingProjectId: data.project_id,
      documentType: data.type,
      error: data.error,
      error_type: data.error_type,
    });

    const documentType = data.type as DocumentType;
    const documentConfig = DOCUMENT_TYPES[documentType as keyof typeof DOCUMENT_TYPES];

    if (!documentConfig) {
      console.error(`Unknown document type: ${data.type}`);
      return;
    }

    const isMatchingProject = data.project_id === currentProject?.id;

    const getErrorMessage = (): string => {
      if (data.error_type === "REQUIREMENTS_NOT_MET") {
        if (data.type === "plugin-proposal") {
          return `${documentConfig.name}の生成に失敗しました。機能要件書にプラグインの使用に関する記載が必要です。`;
        }
        if (data.type === "customize-specification") {
          return `${documentConfig.name}の生成に失敗しました。アプリ設計書にカスタマイズの使用に関する記載が必要です。`;
        }
      }
      return `${documentConfig.name}の生成に失敗しました`;
    };

    if (isMatchingProject) {
      console.log("[webpubsub] Processing failure message - dispatching to Redux");
      dispatch(
        setCurrentProjectOutputsDocumentType({
          documentType,
          documentId: undefined,
          isGenerating: false,
        }),
      );
      toast.error(getErrorMessage(), {
        toastId: `generation-failure-${data.type}-${data.project_id}`,
      });
      refreshOrganization();
      if (currentProject && currentUser) {
        sendNotificationWithNavigation({
          title: PUSH_NOTIFICATION_MESSAGES.DOCUMENT_GENERATION.FAILURE_TITLE,
          message: PUSH_NOTIFICATION_MESSAGES.DOCUMENT_GENERATION.FAILURE_SAME_PROJECT(
            currentProject.name,
            documentConfig.name,
          ),
          organization_id: currentUser.organization_id,
          project_id: currentProject.id,
          document_type: data.type,
          url: `/${currentUser.organization_id}/project/${currentProject.id}/dashboard`,
        });
      }
    } else {
      console.log(
        `[webpubsub] Showing failure notification for different project: ${data.project_id} (current: ${currentProject?.id})`,
      );
      if (currentUser) {
        const notificationUrl =
          currentUser.organization_id && data.project_id
            ? `/${currentUser.organization_id}/project/${data.project_id}/dashboard`
            : undefined;
        sendNotificationWithNavigation({
          title: PUSH_NOTIFICATION_MESSAGES.DOCUMENT_GENERATION.FAILURE_TITLE,
          message: PUSH_NOTIFICATION_MESSAGES.DOCUMENT_GENERATION.FAILURE_OTHER_PROJECT(
            documentConfig.name,
          ),
          organization_id: currentUser.organization_id,
          project_id: data.project_id,
          document_type: data.type,
          ...(notificationUrl && { url: notificationUrl }),
        });
      }
    }
  };

  useEffect(() => {
    if (client === null) return;
    const cleanup = registerGroupMessageHandler(
      client,
      (data) => successHandlerRef.current(data),
      (data) => failureHandlerRef.current(data),
    );
    setClient(client);
    return cleanup;
  }, [client, setClient]);
};
